/**
 * STRIP-01 — the Answer Strip (Seth 2026-08-21: "Strip + A for sure").
 *
 * The five facts every call starts with, in big type, always in the same
 * place: Member? Owes money? When are we there next, when were we there last,
 * how old is the gear. Each cell is a door — Member opens Agreements, Balance
 * opens Invoices, the visits peek in place, Equipment opens its tab.
 *
 * Honesty rules carried over: Balance and Member are whole-account (AR and
 * plans follow the CUSTOMER — the cell tooltips say so on multi-property
 * accounts); Equipment respects the viewed property. Nothing here is a new
 * query — every cell reads a cache another card already warms.
 */
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { normalizeStatus } from "@/lib/status";
import { buildEquipmentView } from "@/lib/equipment";
import { useMembershipCard, useMembershipStatus } from "@/features/agreements/api";
import { useCustomerBalance, type CustomerDispatchRow } from "./api";
import { useCustomerEquipment, useAiEquipmentOverrides } from "./CustomerEquipment";
import { resolveWindow } from "@/lib/format";

/** C360-BOARD-01 — the OPEN ITEMS tile: the customer's waiting work in one
 *  glance (a part on order, a return visit in the queue, an install job in
 *  flight). Reads the same rows the Overview's Open-right-now cards show. */
function useOpenItems(custno: string) {
  return useQuery({
    queryKey: ["open_items_tile", custno],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<{ count: number; headline: string; detail: string; warn: boolean }> => {
      const [tickets, jobs] = await Promise.all([
        supabase
          .from("pegasus_dispatch")
          .select("id, lane_key, summary, part_state, part_eta, part_number, scheduled_dispatch")
          .eq("custno", custno)
          .is("resolved_at", null)
          .is("deleted_at", null)
          .limit(20),
        supabase
          .from("pegasus_job")
          .select("id, title, status")
          .eq("custno", custno)
          .not("status", "in", "(closed,cancelled)")
          .limit(10),
      ]);
      const tRows = (tickets.data ?? []) as { id: string; lane_key: string | null; summary: string | null; part_state: string | null; part_eta: string | null; part_number: string | null; scheduled_dispatch: string | null }[];
      const jRows = (jobs.data ?? []) as { id: string; title: string | null; status: string }[];
      const partT = tRows.find((t) => t.part_state && t.part_state !== "in_stock");
      const count = tRows.length + jRows.length;
      if (count === 0) return { count: 0, headline: "Nothing open", detail: "no waiting work", warn: false };
      const headline = partT
        ? partT.part_state === "arrived" ? "Part arrived" : partT.part_state === "needs_ordering" ? "Part to order" : "Part on order"
        : jRows.length > 0
          ? `Install ${jRows[0].status.replace(/_/g, " ")}`
          : `${count} open item${count === 1 ? "" : "s"}`;
      const detail = partT
        ? [partT.part_number ?? partT.summary?.slice(0, 40), partT.part_eta ? `lands ${format(parseISO(partT.part_eta), "EEE")}` : null].filter(Boolean).join(" · ") || "waiting on the part"
        : jRows.length > 0
          ? (jRows[0].title ?? "install job").slice(0, 48)
          : (tRows[0]?.summary ?? "in the queue").slice(0, 48);
      return { count, headline, detail, warn: !!partT && partT.part_state === "needs_ordering" };
    },
  });
}

function money(v: number): string {
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function fmtShort(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    return format(parseISO(iso), "MMM d");
  } catch {
    return iso;
  }
}

/** "3-Year PLUS Plan" → "PLUS" — the strip wants the badge, not the sentence. */
export function shortPlanName(name: string): string {
  // Seth 2026-08-28: KEEP the term — the bubble should read "3-Yr PLUS", never
  // a bare "PLUS" that hides how long they signed for.
  const s = name
    .replace(/\b(\d+)[- ]?(year|yr)s?\b/gi, "$1-Yr")
    .replace(/\bplans?\b/gi, "")
    .replace(/·/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return s || name;
}

/** Seth 2026-09-10: the Equipment tile says the TYPE first and the year in a
 *  readable size — "Furnace · 2019 · 7 yrs", one line per system. */
interface StatLine { key: string; type: string; year: number | null; age: string | null }

function Stat({
  label,
  value,
  detail,
  lines,
  warn,
  good,
  tone,
  onClick,
  title,
}: {
  label: string;
  value: string;
  detail: string;
  lines?: StatLine[];
  warn?: boolean;
  good?: boolean;
  /** 360-PLAN-TILE: a colored left edge + headline — the plan tile's four states. */
  tone?: "good" | "warn" | "bad" | "muted";
  onClick?: () => void;
  title?: string;
}) {
  const edge = tone === "good" ? "border-l-4 border-l-emerald-500" : tone === "warn" ? "border-l-4 border-l-amber-500" : tone === "bad" ? "border-l-4 border-l-rose-500" : tone === "muted" ? "border-l-4 border-l-slate-300" : "";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      title={title}
      className={cn(
        // NAV-360-04 (Seth: "the four boxes under — could probably be a
        // little shorter"): tighter padding + a 15px value instead of 18px.
        "rounded-xl border px-3 py-1.5 text-left transition-all",
        warn ? "border-amber-200 bg-amber-50/70" : "border-hairline bg-white",
        edge,
        onClick
          ? "hover:-translate-y-px hover:border-brand-300 hover:shadow-e1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 motion-reduce:hover:translate-y-0"
          : "cursor-default",
      )}
    >
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      {lines && lines.length > 0 ? (
        <div className="mt-1 space-y-0.5">
          {lines.map((l) => (
            <div key={l.key} className="flex items-baseline gap-2 text-[13px] leading-tight">
              <span className="min-w-0 truncate font-extrabold tracking-tight text-slate-900">{l.type}</span>
              {l.year ? (
                <span className="ml-auto shrink-0 whitespace-nowrap tabular-nums text-slate-500"><span className="font-semibold text-slate-700">{l.year}</span>{l.age ? ` · ${l.age}` : ""}</span>
              ) : (
                <span className="ml-auto shrink-0 whitespace-nowrap text-[11px] text-slate-400">year not on file</span>
              )}
            </div>
          ))}
          {detail && <div className="truncate text-[11px] text-slate-500">{detail}</div>}
        </div>
      ) : (
        <>
          <div
            className={cn(
              "mt-0.5 truncate text-[15px] font-extrabold leading-tight tracking-tight",
              tone === "good" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : tone === "bad" ? "text-rose-700" : tone === "muted" ? "text-slate-500" : warn ? "text-amber-700" : good ? "text-emerald-700" : "text-slate-900",
            )}
          >
            {value}
          </div>
          <div className="truncate text-[11px] text-slate-500">{detail}</div>
        </>
      )}
    </button>
  );
}

export type AnswerCell = "plan" | "member" | "balance" | "next" | "last" | "equipment" | "open";
const ALL_CELLS: AnswerCell[] = ["member", "balance", "next", "last", "equipment"];

/** "Oct 2027" — month granularity, the way the plan is talked about. */
function monthYear(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    return format(parseISO(iso), "MMM yyyy");
  } catch {
    return iso;
  }
}

export function AnswerStrip({
  custno,
  locno,
  rows,
  multiProperty,
  onPeek,
  cells = ALL_CELLS,
  className,
}: {
  custno: string;
  locno?: string | null;
  /** The customer's dispatch rows (already location-filtered by the Overview). */
  rows: CustomerDispatchRow[];
  multiProperty?: boolean;
  onPeek: (dispatch: string) => void;
  /** 360-B: the rail carries Member + Balance, so the work column shows only
   *  Next / Last / Equipment above the tabs. Default = all five. */
  cells?: AnswerCell[];
  className?: string;
}) {
  const navigate = useNavigate();
  const { data: open, isLoading: oLoading } = useOpenItems(custno);
  const { data: m, isLoading: mLoading } = useMembershipCard(custno);
  // 360-PLAN-TILE (Seth 2026-09-17: "quick glance seeing if a customer is on a
  // membership doesn't really stick out"): the Plan tile reads the ONE lifecycle
  // every screen reads (pegasus_membership_status) — it also knows the last
  // plan of a lapsed or cancelled account, which the card hook does not.
  const { data: life, isLoading: lifeLoading } = useMembershipStatus(custno);
  const { data: bal, isLoading: bLoading } = useCustomerBalance(custno);
  const { data: equip } = useCustomerEquipment(custno, locno);
  const aiOverrides = useAiEquipmentOverrides(equip ?? []).data;
  const systems = (equip ? buildEquipmentView(equip, aiOverrides) : null)?.systems ?? [];

  const today = new Date().toISOString().slice(0, 10);
  const next = rows
    .filter((r) => !!r.appointment_date && r.appointment_date >= today && normalizeStatus(r.status) !== "complete")
    .sort((a, b) => a.appointment_date.localeCompare(b.appointment_date))[0];
  const last = rows.find((r) => !!r.appointment_date && r.appointment_date <= today);

  // Member cell (Seth 2026-09-02: "should show the plan length as the primary
  // text (1 Year, etc). Then… expires"): the headline is the TERM — "1 Year",
  // "3 Years" — never the plan's task list, which truncated ("Furnace, A/C,
  // Humidifi…"). The detail is the expiry date; the full plan name rides in
  // the hover title and lives on the Agreements tab.
  // MEMBERSHIP-ESC-FULFILLED: 'fulfilled' is a member too — paid through, every check used.
  const member = m && (m.status === "active" || m.status === "expiring" || m.status === "fulfilled");
  const termText = (() => {
    const src = `${m?.termLabel ?? ""} ${m?.planName ?? ""}`;
    const n = src.match(/(\d+)\s*-?\s*Year/i)?.[1];
    return n ? `${n} Year${n === "1" ? "" : "s"}` : m ? shortPlanName(m.planName) : "";
  })();
  const memberValue = mLoading ? "…" : member ? termText : "None";
  const memberDetail = member
    ? [
        m.expireDate ? `${m.status === "fulfilled" ? "paid through" : "expires"} ${fmtShort(m.expireDate)}` : null,
        m.status === "fulfilled" ? "all visits used" : null,
        m.secondSystem ? "2 systems" : null,
        m.status !== "fulfilled" && m.planVisits ? `${m.planVisits.used} of ${m.planVisits.total} visits` : null,
      ]
        .filter(Boolean)
        .slice(0, 2)
        .join(" · ")
    : m && m.status === "ended"
      ? `ended ${fmtShort(m.expireDate) ?? ""} · ${m.checksOwed} check${m.checksOwed === 1 ? "" : "s"} owed`
      : m && m.status === "expired"
        ? `expired ${fmtShort(m.expireDate) ?? ""}`
        : "no active plan";

  // Plan tile — four states, one line each. The plan's TERM is the headline
  // of a live plan ("3-Year Plan" — Seth 2026-10-21: "I'd rather it display
  // the plan year length"; the renewal date lives on the Membership tab, not
  // here), the month is the headline of anything ending. "PLUS" is not said
  // on the tile (PLUS-WORDING-01 — the vocabulary is moving to "plan").
  const planTerm = life?.term_years ? `${life.term_years}-Year` : null;
  const planLabel = planTerm ? `${planTerm} Plan` : "Plan";
  const owedText = life
    ? life.checks_owed > 0
      ? `${life.checks_owed} tune-up${life.checks_owed === 1 ? "" : "s"} owed`
      : "no tune-ups owed"
    : "";
  const plan: { value: string; detail: string; tone: "good" | "warn" | "bad" | "muted" } = lifeLoading
    ? { value: "…", detail: "", tone: "muted" }
    : !life
      ? { value: "Not on a plan", detail: "never had one · sell one →", tone: "muted" }
      : life.status === "active"
        ? { value: planLabel, detail: owedText, tone: "good" }
        : life.status === "expiring"
          ? { value: `Expires ${fmtShort(life.expire_date) ?? ""}`.trim(), detail: [planLabel, owedText].join(" · "), tone: "warn" }
          // MEMBERSHIP-ESC-FULFILLED: every check used, paid through the date — the renewal moment, not a cancellation.
          : life.status === "fulfilled"
            ? { value: planLabel, detail: `all tune-ups used · paid through ${fmtShort(life.expire_date) ?? "—"}`, tone: "warn" }
          : life.status === "ended"
            ? { value: `Term ended ${monthYear(life.expire_date) ?? ""}`.trim(), detail: `${owedText}${life.owed_until ? ` until ${monthYear(life.owed_until)}` : ""}`, tone: "warn" }
            : life.status === "cancelled"
              ? { value: "Plan cancelled", detail: [`was a ${planLabel}`, "no member pricing"].join(" · "), tone: "bad" }
              : { value: "Not on a plan", detail: `last plan ended ${monthYear(life.expire_date) ?? "—"} · ${life.status === "lapsed" ? "win-back →" : "sell one →"}`, tone: "muted" };

  // Balance cell — total owed + the worst aging note.
  const over60 = (bal?.buckets.d60 ?? 0) + (bal?.buckets.d90 ?? 0);
  const owes = (bal?.total ?? 0) > 0.005;
  const balValue = bLoading ? "…" : !bal?.mirrored ? "—" : owes ? money(bal.total) : "Paid Up";
  const balDetail = !bal?.mirrored
    ? "AR not mirrored yet"
    : owes
      ? over60 > 0
        ? `${money(over60)} over 60 days`
        : (bal.buckets.d30 ?? 0) > 0
          ? `${money(bal.buckets.d30)} over 30 days`
          : "all current"
      : "nothing owed";

  // Equipment cell — the TYPE first (Furnace, A/C…) with the install year and
  // a plain-English age, one readable line per system (Seth 2026-09-10: "say
  // more prominently what type of equipment … and then a nicer installed year").
  const yearNow = new Date().getFullYear();
  const age = (y: number) => (yearNow - y <= 1 ? "new" : `${yearNow - y} yrs`);
  // The two lines are the heating source and the cooling side (a coil rides
  // with its A/C and never spends a line when the A/C is shown), newest first
  // within a kind; the rest count as "+N more".
  const RANK: Record<string, number> = { furnace: 0, boiler: 0, "heat-pump": 1, ac: 2, "mini-split": 3, rooftop: 3, "magic-pak": 3, "garage-heater": 4, "air-handler": 5, "water-heater": 6, coil: 9 };
  const ranked = [...systems].sort((a, b) => (RANK[a.category] ?? 7) - (RANK[b.category] ?? 7) || (b.installYear ?? 0) - (a.installYear ?? 0));
  const hasCooling = ranked.some((s) => s.category === "ac" || s.category === "heat-pump");
  const picked = ranked.filter((s) => !(s.category === "coil" && hasCooling)).slice(0, 2);
  const eqLines: StatLine[] = picked.map((s, i) => ({
    key: `${s.counter ?? i}`,
    type: s.categoryLabel,
    year: s.installYear ?? null,
    age: s.installYear ? age(s.installYear) : null,
  }));
  const eqValue = systems.length === 0 ? "None" : `${systems.length} system${systems.length > 1 ? "s" : ""}`;
  const eqRest = systems.length - picked.length;
  const eqDetail = systems.length === 0 ? "nothing on record" : eqRest > 0 ? `+${eqRest} more on the Equipment tab` : "";

  const allProps = multiProperty ? " — whole account, all properties" : "";

  const cols: Record<number, string> = { 1: "lg:grid-cols-1", 2: "lg:grid-cols-2", 3: "lg:grid-cols-3", 4: "lg:grid-cols-4", 5: "lg:grid-cols-5", 6: "lg:grid-cols-6" };
  // C360-BOARD-01: the tiles render in the ORDER the caller asked for —
  // the board reads NEXT VISIT · OPEN ITEMS · BALANCE · PLAN left to right.
  const cell = (c: AnswerCell): React.ReactNode => {
    switch (c) {
      case "plan": return <Stat
        label="Plan"
        value={plan.value}
        detail={plan.detail}
        tone={plan.tone}
        title={`${life?.plan_name ? `${life.plan_name}${allProps}` : `Membership plan${allProps}`} — opens Memberships`}
        onClick={() => navigate(`/customers/${custno}?tab=agreements`)}
      />;
      case "member": return <Stat
        label="Member"
        value={memberValue}
        detail={memberDetail}
        good={!!member}
        title={`${member ? `${m.planName}${allProps}` : `Membership plan${allProps}`} — opens Agreements`}
        onClick={() => navigate(`/customers/${custno}?tab=agreements`)}
      />;
      case "balance": return <Stat
        label="Balance"
        value={balValue}
        detail={balDetail}
        warn={owes}
        good={!!bal?.mirrored && !owes}
        title={`Owed${allProps} — opens Invoices`}
        onClick={() => navigate(`/customers/${custno}?tab=invoices`)}
      />;
      case "open": return <Stat
        label="Open Items"
        value={oLoading ? "…" : (open?.headline ?? "Nothing open")}
        detail={open?.detail ?? ""}
        tone={open && open.count > 0 ? (open.warn ? "bad" : "warn") : "muted"}
        title="Waiting work — parts, return visits, open installs. Opens the Service tab."
        onClick={() => navigate(`/customers/${custno}?tab=service`)}
      />;
      case "next": return <Stat
        label="Next Visit"
        value={next ? (fmtShort(next.appointment_date) ?? "—") : "None"}
        detail={
          next
            ? [resolveWindow(next.window_code, next.notes).value, next.tech_first ?? next.tech_name].filter(Boolean).join(" · ") || "booked"
            : "nothing booked"
        }
        title={next ? "Opens the visit" : "No upcoming visit scheduled"}
        onClick={next ? () => onPeek(next.dispatch) : undefined}
      />;
      {/* Last Visit lands on the HISTORY tab with that visit ringed, scrolled
          into view and peeked (Seth 2026-09-17: "have last visit tab up top
          take you to the history tab — and then you can load that visit") —
          the visit in the context of everything around it, not a bare peek. */}
      case "last": return <Stat
        label="Last Visit"
        value={last ? (fmtShort(last.appointment_date) ?? "—") : "None"}
        detail={last ? [last.tech_first ?? last.tech_name, normalizeStatus(last.status) !== "complete" ? last.status : null].filter(Boolean).join(" · ") || "on record" : "no visits yet"}
        title={last ? "Opens the visit on the History tab" : "No past visits on record"}
        onClick={last ? () => navigate(`/customers/${custno}?tab=history&visit=${encodeURIComponent(last.dispatch)}`) : undefined}
      />;
      case "equipment": return <Stat
        label="Equipment"
        value={eqValue}
        detail={eqDetail}
        lines={eqLines}
        title="Systems at this property — opens Equipment"
        onClick={() => navigate(`/customers/${custno}?tab=equipment`)}
      />;
    }
  };
  return (
    <div className={cn("grid grid-cols-2 gap-2.5 sm:grid-cols-3", cols[cells.length] ?? "lg:grid-cols-5", className ?? "mb-4")}>
      {cells.map((c) => <span key={c} className="contents">{cell(c)}</span>)}
    </div>
  );
}
